import React, { createContext, useContext, useEffect, useState } from 'react';
import { PLATFORM_ORIGIN, API_BASE_URL } from '../config';

interface AuthState {
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  token: null, isLoading: true,
  login: async () => 'Not ready', logout: () => undefined,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('access_token');
    if (stored) setToken(stored);

    const handler = (event: MessageEvent) => {
      if (event.origin !== PLATFORM_ORIGIN) return;
      if (event.data?.type === 'auth' && event.data?.token) {
        localStorage.setItem('access_token', event.data.token);
        setToken(event.data.token);
      }
    };
    window.addEventListener('message', handler);
    setIsLoading(false);
    return () => window.removeEventListener('message', handler);
  }, []);

  const login = async (email: string, password: string): Promise<string | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) { const e = await res.json(); return e?.detail ?? 'Login failed'; }
      const data = await res.json();
      localStorage.setItem('access_token', data.access_token);
      setToken(data.access_token);
      return null;
    } catch { return 'Network error'; }
  };

  const logout = () => { localStorage.removeItem('access_token'); setToken(null); };

  return (
    <AuthContext.Provider value={{ token, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
